import React, { useCallback, useRef } from 'react';
import { useDraggablePosition } from '../../hooks/useDraggablePosition';
import { usePanelSize } from '../../hooks/usePanelSize';
import { defaultPanelPosition } from '../../ui/panelLayout';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { startPointerDrag } from '../../Utils/pointerDrag';

const HANDLE_CLASS =
  'absolute z-20 pointer-events-auto touch-none select-none';

/**
 * Fixed floating panel with drag handle (+ optional edge resize).
 * Size can be controlled via `size`/`onSizeChange`, or unmanaged via internal storage.
 */
export default function DraggablePanel({
  id,
  getDefaultPosition,
  children,
  className = '',
  style,
  handleLabel = 'Drag',
  zIndex = 1200,
  resizable = false,
  defaultSize,
  size: controlledSize,
  onSizeChange,
  minWidth = 240,
  minHeight = 180,
  maxWidth,
  maxHeight,
  resizeEdges = ['e', 's', 'se', 'w'],
  handleTitle = 'Drag to move — position is saved',
  onDragMove,
  onDragEnd,
}) {
  const compact = useCompactLayout();
  const panelRef = useRef(null);
  const { pos, setPos, onDragHandlePointerDown } = useDraggablePosition(
    id,
    getDefaultPosition || (() => defaultPanelPosition(id)),
    { onDragMove, onDragEnd, nodeRef: panelRef }
  );

  const getDefaultSize = useCallback(() => {
    if (typeof defaultSize === 'function') return defaultSize();
    if (defaultSize) return defaultSize;
    return {
      width: 320,
      height: typeof window !== 'undefined' ? Math.min(720, Math.round(window.innerHeight * 0.85)) : 480,
    };
  }, [defaultSize]);

  const internal = usePanelSize(
    resizable && controlledSize == null ? id : null,
    getDefaultSize,
    {
      minW: minWidth,
      minH: minHeight,
      maxW: maxWidth,
      maxH: maxHeight,
    }
  );

  const size = controlledSize || internal.size;
  const setSize = onSizeChange || internal.setSize;

  const onEdgePointerDown = (edge) => (e) => {
    if (e.button != null && e.button !== 0) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const originSize = { ...size };
    const originPos = { ...pos };

    startPointerDrag(e, {
      onMove: (ev) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        const maxW = maxWidth ?? Math.max(minWidth, window.innerWidth - 24);
        const maxH = maxHeight ?? Math.max(minHeight, window.innerHeight - 48);

        let width = originSize.width;
        let height = originSize.height;
        let left = originPos.left;
        let top = originPos.top;

        if (edge.includes('e')) {
          width = Math.min(maxW, Math.max(minWidth, originSize.width + dx));
        }
        if (edge.includes('w')) {
          const nextW = Math.min(maxW, Math.max(minWidth, originSize.width - dx));
          left = originPos.left + (originSize.width - nextW);
          width = nextW;
        }
        if (edge.includes('s')) {
          height = Math.min(maxH, Math.max(minHeight, originSize.height + dy));
        }
        if (edge.includes('n')) {
          const nextH = Math.min(maxH, Math.max(minHeight, originSize.height - dy));
          top = originPos.top + (originSize.height - nextH);
          height = nextH;
        }

        setSize({ width, height });
        if (edge.includes('w') || edge.includes('n')) {
          setPos({ left, top });
        }
      },
    });
  };

  return (
    <div
      ref={panelRef}
      className={`fixed flex flex-col ${compact ? 'mdv-compact-float' : ''} ${className}`}
      style={
        compact
          ? { zIndex, ...style }
          : {
              left: pos.left,
              top: pos.top,
              zIndex,
              ...(resizable
                ? {
                    width: size.width,
                    height: size.height,
                    maxWidth: 'calc(100vw - 16px)',
                    maxHeight: 'calc(100vh - 16px)',
                  }
                : null),
              ...style,
              ...(resizable ? { width: size.width, height: size.height } : null),
            }
      }
    >
      <div
        role="presentation"
        onPointerDown={compact ? undefined : onDragHandlePointerDown}
        className={`flex items-center gap-1.5 select-none touch-none px-2 py-1.5 min-h-8 rounded-t-md border border-b-0 border-yellow-600/35 bg-zinc-950/95 text-yellow-600/80 shrink-0 ${
          compact ? '' : 'cursor-grab active:cursor-grabbing'
        }`}
        style={compact ? undefined : { touchAction: 'none' }}
        title={compact ? handleLabel : handleTitle}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <circle cx="5" cy="4" r="1.2" />
          <circle cx="11" cy="4" r="1.2" />
          <circle cx="5" cy="8" r="1.2" />
          <circle cx="11" cy="8" r="1.2" />
          <circle cx="5" cy="12" r="1.2" />
          <circle cx="11" cy="12" r="1.2" />
        </svg>
        <span className="text-[8px] font-semibold uppercase tracking-[0.16em]">{handleLabel}</span>
        {resizable && !compact && (
          <span className="ml-auto text-[7px] uppercase tracking-wider text-zinc-600">Drag edges to resize</span>
        )}
      </div>
      <div className="relative min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden">{children}</div>

      {resizable && !compact &&
        resizeEdges.map((edge) => {
          const common = {
            key: edge,
            role: 'separator',
            onPointerDown: onEdgePointerDown(edge),
            style: { touchAction: 'none' },
          };
          if (edge === 'e') {
            return (
              <div
                {...common}
                aria-orientation="vertical"
                aria-label="Resize width"
                className={`${HANDLE_CLASS} top-0 right-0 w-1.5 h-full cursor-ew-resize hover:bg-yellow-500/35`}
              />
            );
          }
          if (edge === 'w') {
            return (
              <div
                {...common}
                aria-orientation="vertical"
                aria-label="Resize width"
                className={`${HANDLE_CLASS} top-0 left-0 w-1.5 h-full cursor-ew-resize hover:bg-yellow-500/35`}
              />
            );
          }
          if (edge === 's') {
            return (
              <div
                {...common}
                aria-orientation="horizontal"
                aria-label="Resize height"
                className={`${HANDLE_CLASS} bottom-0 left-0 h-1.5 w-full cursor-ns-resize hover:bg-yellow-500/35`}
              />
            );
          }
          if (edge === 'n') {
            return (
              <div
                {...common}
                aria-orientation="horizontal"
                aria-label="Resize height"
                className={`${HANDLE_CLASS} top-0 left-0 h-1.5 w-full cursor-ns-resize hover:bg-yellow-500/35`}
              />
            );
          }
          if (edge === 'se') {
            return (
              <div
                {...common}
                aria-label="Resize"
                title="Drag to resize"
                className={`${HANDLE_CLASS} bottom-0 right-0 h-3.5 w-3.5 cursor-nwse-resize`}
              >
                <svg
                  viewBox="0 0 12 12"
                  className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 text-yellow-600/80"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden
                >
                  <path d="M10 2v8H2" />
                  <path d="M10 6v4H6" />
                </svg>
              </div>
            );
          }
          return null;
        })}
    </div>
  );
}
